import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Download, Eye, Loader2, Search, ChevronUp, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import { FetchAllUsersAction } from "../../Store/Actions/UserActions";
import { handleAuthError } from "../../utils/authUtils";
import { downloadUsersCsv } from "../../utils/downloadCsv";
import BlockReasonModal from "./BlockReasonModal";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const STATUS_STYLES = {
  ACTIVE: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-800",
  PENDING: "bg-yellow-100 text-yellow-800",
  BLOCKED: "bg-orange-100 text-orange-800",
};

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <ChevronUp size={12} className="text-gray-300 ml-1 inline" />;
  return sortDir === "asc"
    ? <ChevronUp size={12} className="text-themeColor ml-1 inline" />
    : <ChevronDown size={12} className="text-themeColor ml-1 inline" />;
};

const TABS = ["All", "ACTIVE", "REJECTED", "BLOCKED"];

const AllNonProfitUsers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userState = useSelector((state) => state.user);
  const loader = userState?.loader ?? false;
  const allUsers = userState?.allUsers;
  const allUsersError = userState?.allUsersError ?? null;

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("All");
  const [exporting, setExporting] = useState(false);
  const [blockModal, setBlockModal] = useState(null); // { userId, userName, isBlocking }
  const [blockingId, setBlockingId] = useState(null);

  useEffect(() => { dispatch(FetchAllUsersAction()); }, [dispatch]);

  const handleBlockToggle = (user) => {
    const isBlocking = user.activationStatus !== "BLOCKED";
    const userName = user.nonProfitUser?.organizationName || user.email;
    setBlockModal({ userId: user.id, userName, isBlocking });
  };

  const handleBlockConfirm = async (reason) => {
    const { userId, isBlocking } = blockModal;
    setBlockModal(null);
    setBlockingId(userId);
    try {
      const token = localStorage.getItem("accessToken");
      const endpoint = isBlocking
        ? `${BASE_URL}${API_ENDPOINTS.BLOCK_USER(userId)}`
        : `${BASE_URL}${API_ENDPOINTS.UNBLOCK_USER(userId)}`;
      const res = await fetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: isBlocking ? JSON.stringify({ reason }) : undefined,
      });
      const result = await res.json();
      if (result.statusCode === 200) {
        toast.success(isBlocking ? "User blocked successfully" : "User unblocked successfully");
        dispatch(FetchAllUsersAction());
      } else {
        toast.error(result.message || "Action failed");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setBlockingId(null);
    }
  };

  useEffect(() => {
    if (allUsersError) {
      if (handleAuthError(allUsersError, null, dispatch)) return;
      toast.error(allUsersError.message || "Failed to fetch users.");
    }
  }, [allUsersError, dispatch]);

  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadUsersCsv('nonprofit');
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    // Only NP users, exclude PENDING (those are in the approval tab)
    let list = (allUsers || []).filter(
      (u) => u.nonProfitUser !== null && u.nonProfitUser !== undefined && u.activationStatus !== "PENDING"
    );

    if (statusFilter !== "All") {
      list = list.filter((u) => u.activationStatus === statusFilter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((u) => {
        const org = u.nonProfitUser?.organizationName?.toLowerCase() || "";
        return u.email?.toLowerCase().includes(q) || org.includes(q);
      });
    }

    list.sort((a, b) => {
      let av, bv;
      if (sortField === "name") {
        av = (a.nonProfitUser?.organizationName || "").toLowerCase();
        bv = (b.nonProfitUser?.organizationName || "").toLowerCase();
      } else {
        av = new Date(a.createdAt).getTime();
        bv = new Date(b.createdAt).getTime();
      }
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

    return list;
  }, [allUsers, search, sortField, sortDir, statusFilter]);

  return (
    <div className="w-[95%] mx-auto">
      {blockModal && (
        <BlockReasonModal
          isBlocking={blockModal.isBlocking}
          userName={blockModal.userName}
          onConfirm={handleBlockConfirm}
          onCancel={() => setBlockModal(null)}
        />
      )}
      <div className="my-6">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-popinsSemiBold text-xl lg:text-2xl mb-1">All Non-Profit Users</h1>
            <p className="text-black/50 font-popinsRegular text-sm">
              Browse and manage all registered non-profit organizations
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-themeColor text-white text-sm font-popinsMedium hover:bg-opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Download size={15} />
            {exporting ? 'Exporting…' : 'Export to CSV'}
          </button>
        </div>

        {/* Search bar */}
        <div className="relative max-w-sm mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by organization or email…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg font-popinsRegular focus:outline-none focus:border-themeColor"
          />
        </div>

        {/* Status tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setStatusFilter(tab)}
              className={`px-4 py-2 text-sm font-popinsMedium border-b-2 transition-colors ${
                statusFilter === tab
                  ? "border-themeColor text-themeColor"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab === "All" ? "All" : tab.charAt(0) + tab.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        {!loader && filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-textColor font-popinsMedium">
              {allUsers.filter((u) => u.nonProfitUser).length === 0
                ? "No non-profit users found"
                : "No results match your search"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider cursor-pointer select-none w-[26%]"
                      onClick={() => toggleSort("name")}
                    >
                      Organization <SortIcon field="name" sortField={sortField} sortDir={sortDir} />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[24%]">
                      Email
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[10%]">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[10%]">
                      ZIP
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[14%]">
                      Phone
                    </th>
                    <th
                      className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider cursor-pointer select-none w-[12%]"
                      onClick={() => toggleSort("createdAt")}
                    >
                      Joined <SortIcon field="createdAt" sortField={sortField} sortDir={sortDir} />
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[8%]">
                      Blocked?
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider w-[4%]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loader && (
                    <tr>
                      <td colSpan={8} className="px-6 py-6 text-center">
                        <div className="flex justify-center items-center gap-2 text-gray-500">
                          <Loader2 className="animate-spin" size={20} />
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loader && filtered.map((user) => {
                    const isBlocked = user.activationStatus === "BLOCKED";
                    const isLoadingBlock = blockingId === user.id;
                    return (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsMedium text-gray-900 truncate max-w-[180px]">
                            {user.nonProfitUser?.organizationName || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsRegular text-gray-600 truncate max-w-[160px]">
                            {user.email}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-popinsSemiBold rounded-full ${STATUS_STYLES[user.activationStatus] || "bg-gray-100 text-gray-800"}`}>
                            {user.activationStatus || "N/A"}
                          </span>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsRegular text-gray-500">
                            {user.nonProfitUser?.zipCode || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsRegular text-gray-500">
                            {user.nonProfitUser?.phoneNumber || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsRegular text-gray-500">{formatDate(user.createdAt)}</div>
                        </td>
                        <td className="px-3 py-4 text-center">
                          {isLoadingBlock ? (
                            <Loader2 size={18} className="animate-spin text-gray-400 mx-auto" />
                          ) : (
                            <button
                              onClick={() => handleBlockToggle(user)}
                              title={isBlocked ? "Unblock user" : "Block user"}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                                isBlocked ? "bg-red-500" : "bg-gray-200"
                              }`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                  isBlocked ? "translate-x-[18px]" : "translate-x-[3px]"
                                }`}
                              />
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-4 text-center">
                          <button
                            onClick={() => navigate(`/dashboard/user-detail/${user.id}?from=nonprofit`)}
                            className="text-themeColor hover:text-[#3FC4E3] transition duration-300 ease-in-out transform hover:scale-110 flex items-center justify-center mx-auto"
                            title="View Details"
                          >
                            <Eye size={20} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllNonProfitUsers;
